import { _ } from "../../../core/polyglot/polyglot.js";
import { eventManager } from "@foretagsplatsen/yaem";
import {
	formatAsMonthAndYear,
	formatAsShortDate,
} from "../../../core/helpers/date-formatting-util.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import { spacer, stacker } from "../../../core/elements/index.js";
import accountingDocument from "../../shared/accountingDocument.js";
import ChecklistItem from "../../../models/accounting/periodReport/checklist/ChecklistItem.js";
import FlipWidget from "../../../core/widgets/FlipWidget/FlipWidget.js";
import FormBuilder from "../../../core/widgets/forms/FormBuilder.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import HelpWizard from "../../../core/chrome/HelpWizard/HelpWizard.js";
import InlineEditorWidget from "../../../core/widgets/InlineEditorWidget/InlineEditorWidget.js";
import InputValueActionBuilder from "../../../models/wysiwygEditor/InputValueActionBuilder.js";
import KeyFigureCalculationService from "../../keyFigure/editor/KeyFigureCalculationService.js";
import KeyFigureContextRepositoryWrapper from "../../../core/repositories/KeyFigureContextRepositoryWrapper.js";
import KeyFigurePresetService from "../../../core/services/KeyFigurePresetService.js";
import makeAccountInputValuePopover from "../../../core/widgets/WysiwygEditorWidget/inputValuePopovers/makeAccountInputValuePopover.js";
import makeKeyFigureInputValuePopover from "../../../core/widgets/WysiwygEditorWidget/inputValuePopovers/makeKeyFigureInputValuePopover.js";
import makePeriodInputValuePopover from "../../../core/widgets/WysiwygEditorWidget/inputValuePopovers/makePeriodInputValuePopover.js";
import makeReportNotificationButton from "./makeReportNotificationButton.js";
import notifier from "../../../core/widgets/notifier/notifier.js";
import optionsBag from "../../../models/options/optionsBag.js";
import PeriodReportChecklistWidget from "./PeriodReportChecklistWidget/PeriodReportChecklistWidget.js";
import PeriodReportCommentEditorWidget from "./PeriodReportCommentEditorWidget.js";
import PeriodReportData from "../../../models/accounting/periodReport/condensedReport/PeriodReportData.js";
import PeriodReportDefaultText from "../../../models/accounting/periodReport/PeriodReportDefaultText.js";
import PeriodReportDraft from "../../../models/accounting/periodReport/PeriodReportDraft.js";
import PeriodReportNotificationService from "./PeriodReportNotificationService.js";
import PeriodReportSectionEditorVisitor from "./PeriodReportSectionEditorVisitor.js";
import PeriodReportSectionPickerWidget from "./PeriodReportSectionPickerWidget.js";
import PeriodReportService from "./PeriodReportService.js";
import PeriodReportSettings from "../../../models/PeriodReportSettings.js";
import PeriodSelectionSectionWidget from "./PeriodSelectionSectionWidget/PeriodSelectionSectionWidget.js";
import PeriodService from "../../shared/PeriodService.js";
import ReportTemplate from "../../../models/reports/templates/ReportTemplate.js";
import session from "../../../core/session.js";
import SwitchButton from "../../../core/widgets/SwitchButton/SwitchButton.js";
import TabWidget from "../../../core/widgets/TabWidget/TabWidget.js";
import TitleWidget from "../../../core/chrome/TitleWidget2.js";
import UploadService from "../../../core/services/UploadService.js";

/**
 * `periodReportEditorDocument` is the document used to create (and
 * preview) a new report.
 */
function periodReportEditorDocument(spec, my) {
	spec ||= {};
	my ||= {};
	const that = accountingDocument(spec, my);

	that.documentId = "create-period-report period-report";

	that.help = function () {
		return [
			{
				title: _("Period report editor"),
				content: _(
					"The period report editor enables you to create reports where you can mix a financial summary with your own comments. Unlike other reports and tables, period reports provide you the ability to clarify numbers with your own words. That's why the period report editor is an awesome tool to keep your clients up-to-date with their financial status.",
				),
			},
			{
				title: _("Drafts"),
				content: _(
					'One can save "work in progress" by clicking save draft. A saved draft will then be found in the archive by you or your colleagues for later completion.',
				),
			},
			{
				title: _("Title"),
				content: _(
					"The report title is an option that does not need to be altered, however if one wants another title this can be changed here.",
				),
				element: "document-title",
			},
			{
				title: _("Subtitle"),
				content: _(
					"A subtitle is by default given but can be changed here.",
				),
				element: "document-subtitle",
			},
			{
				title: _("Period"),
				content: _(
					"The first input field is to select the period that you want to target. Upon your selection, the report will automatically be updated to have the numbers for the selected period. Moreover, a default set of financial statements will be attached.",
				),
				element: "period-form",
			},
			{
				title: _("Comment"),
				content: _(
					"The comment is where the reporter can write a personal note about the period. The report preview will be updated as you type.",
				),
				element: "period-report-comment-editor",
			},
			{
				title: _("KPIs"),
				content: _(
					'You can choose to include KPIs from "My Key Figures" preset in your report. In order to change KPIs, You can edit "My Key Figures" preset by visiting Company settings page.',
				),
				element: "kpis-section",
			},
			{
				title: _("Attachments"),
				content: _(
					"Attachments allows the reporter to include external files to the report. You will also find the default set of attachments in this section. The report preview will be updated when you add or remove attachments.",
				),
				element: "attachments-editor-section",
			},
			{
				title: _("Publish"),
				content: _(
					"By publishing a period report you will make the report available for the client user in the period report archive. A publish report can not be deleted or edited. It can only be withdrawn.",
				),
				element: "publish",
			},
		];
	};

	that.title = (html) => {
		if (my.draft) {
			html.render(my.draft.getTitle());
		} else {
			html.render(_("Create period report"));
		}
	};

	that.subtitle = (html) => html.render(my.displaySubtitle());

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.hasWideActionsMenu = function () {
		return my.hasNonEmptyChecklist();
	};

	that.actions = function () {
		if (my.hasNonEmptyChecklist()) {
			return my.allActions();
		}
		return my.editActionsRenderer();
	};

	my.companyId = session.getCompanyId();

	my.isContentLoading = false;

	my.configuration = null;
	my.agencyUsers = [];

	my.allActions = function () {
		return [
			new TabWidget({
				tabs: [
					{
						name: _("Edit"),
						id: "edit",
						content: my.editActionsRenderer(),
					},
					{
						name: _("Checklist"),
						id: "check-list",
						content: (html) => {
							html.div(
								{ klass: "right-menu-section" },
								my.checklistEditor,
							);
						},
					},
				],
			}),
		];
	};

	const antiForgeryToken = session.getAntiForgeryToken();

	my.periodReportRepository = new KeyFigureContextRepositoryWrapper({
		keyFigureService: getKeyFigureService(),
		repository: makeMonitorRepository({
			model: PeriodReportDraft,
			route: "/Accounting2/Company/#companyId/PeriodReport/?action/?id",
			defaultFilters: {
				companyId: my.companyId,
			},
			antiForgeryToken,
		}),
	});

	my.periodReportDataRepository = new KeyFigureContextRepositoryWrapper({
		keyFigureService: getKeyFigureService(),
		repository: makeMonitorRepository({
			model: PeriodReportData,
			route: "/Accounting2/Company/#companyId/PeriodReportData",
			defaultFilters: {
				companyId: my.companyId,
			},
			antiForgeryToken,
		}),
	});

	my.periodReportSettingsRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/PeriodReportSettings",
		model: PeriodReportSettings,
		defaultFilters: {
			companyId: my.companyId,
		},
		antiForgeryToken,
	});

	my.defaultTextRepository = makeMonitorRepository({
		route: "/Shared/CompanyType/#companyTypeId/PeriodReportDefaultText",
		model: PeriodReportDefaultText,
		defaultFilters: {
			companyTypeId: session.getCompany().getCompanyTypeId(),
		},
		antiForgeryToken,
	});

	my.resourceRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/PeriodReport/#periodReportId/Resource/#guid",
		defaultFilters: {
			companyId: my.companyId,
		},
		antiForgeryToken,
	});

	my.customReportRepository = makeMonitorRepository({
		route: "/Shared/ReportTemplate/Company/#companyId/?id",
		model: ReportTemplate,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken,
	});

	my.uploadFileService = new UploadService();

	my.notificationService = new PeriodReportNotificationService({
		companyId: my.companyId,
	});

	my.toggleChecklistItemRepository = makeMonitorRepository({
		route: "/Accounting2/Company/#companyId/PeriodReport/#periodReportId/Checklist/Section/#sectionId/Item/#itemId",
		model: ChecklistItem,
		defaultFilters: {
			companyId: my.companyId,
		},
		antiForgeryToken,
	});

	my.titleEditor = new InlineEditorWidget({
		getContent: () => (my.draft ? my.draft.getTitle() : ""),
		placeholder: _("Create period report"),
	});

	my.titleEditor.contentChanged.register((content) => {
		my.draft.setTitle(content);
	});

	my.subtitleEditor = new InlineEditorWidget({
		getContent: () => (my.draft ? my.displaySubtitle() : ""),
	});

	my.displaySubtitle = function () {
		if (!my.draft) {
			return "";
		}

		if (my.draft.getSubtitle()) {
			return my.draft.getSubtitle();
		}
		return _("Made by {author} in {date} for {company}", {
			author: my.draft.getAuthor().getName(),
			date: formatAsMonthAndYear(my.draft.getLastUpdateDate()),
			company: my.draft.getCompany().getName(),
		});
	};

	my.subtitleEditor.contentChanged.register((content) => {
		my.draft.setSubtitle(content);
	});

	my.titleWidget = new TitleWidget({
		title: my.titleEditor,
		subtitle: my.subtitleEditor,
		helpWizard: new HelpWizard({
			stepsBuilder: that.help,
		}),
		helpEnabled: that.hasHelp(),
	});

	my.periodReportTitle = new TitleWidget({
		id: "preview-document-title",
		title: (html) => {
			html.render(my.draft.getTitle());
		},
		subtitle: (html) => {
			html.render(my.draft.getSubtitle());
		},
	});

	my.checklistEditor = new PeriodReportChecklistWidget({
		toggleItemCallback: (...rest) => my.toggleChecklistItem(...rest),
		insertCommentCallback: (...rest) => my.insertComment(...rest),
		sectionTitleRenderer: (...rest) => my.getSectionTitle(...rest),
		isSequential: () =>
			!my.configuration ||
			my.configuration.isPeriodReportChecklistSequential(),
	});

	my.getPeriodReportValuesKeyFigurePreset = function () {
		my.keyFigurePresetPromise ??= new KeyFigurePresetService({
			companyId: session.getCompanyId(),
			keyFigureService: getKeyFigureService(),
		}).getVisibleKeyFigures();

		return my.keyFigurePresetPromise;
	};

	my.inputValueActionBuilder = new InputValueActionBuilder({
		periodService: new PeriodService({
			company: session.getCompany(),
		}),
		makeKeyFigurePopover: makeKeyFigureInputValuePopover(),
		makeAccountPopover: makeAccountInputValuePopover,
		makePeriodPopover: makePeriodInputValuePopover,
		getPeriod: () => my.getPeriod(),
		keyFigureCalculationService: new KeyFigureCalculationService({
			companyId: session.getCompanyId(),
		}),
		getAccountByNumber: (accountNumber) =>
			session.getAccountByNumber(accountNumber),
		getAllAccounts: () =>
			session
				.getAllAccounts()
				.sort((one, another) => one.toString() > another.toString()),
		getPeriodReportValuesKeyFigurePreset:
			my.getPeriodReportValuesKeyFigurePreset,
	});

	my.commentEditor = new PeriodReportCommentEditorWidget({
		avatarUrl: session.getCurrentUser().getPictureUrl(),
		accessor: () => my.draft && my.draft.getComment(),
		mutator: (comment) => my.draft && my.draft.setComment(comment),
		showAvatar: true,
		inputValueActionBuilder: my.inputValueActionBuilder,
		onPictureUploadRequest: (file) => my.uploadFile(file),
		onSourceDeletionRequest: (source) => my.deleteSource(source),
		dataTestName: "main-comment-editor",
	});

	const uploadService = new UploadService();

	my.lastImportDate = formatAsShortDate(
		session.getCompany().getLastImportDate(),
	);

	my.sectionEditorVisitor = new PeriodReportSectionEditorVisitor({
		onAttachmentUpload: uploadService.uploadFile.bind(uploadService),
		canViewAccountingDocuments: session
			.getCurrentUser()
			.canViewAccountingDocuments(session.getCompany()),
		lastImportDate: my.lastImportDate ?? "",
		inputValueActionBuilder: my.inputValueActionBuilder,
		onPictureUploadRequest: (file) => my.uploadFile(file),
		onSourceDeletionRequest: (source) => my.deleteSource(source),
	});

	my.sectionPicker = new PeriodReportSectionPickerWidget();

	my.sectionPicker.sectionsChanged.register(() => {
		my.autoSaveDraft();
		that.update();
	});

	that.show = async function (periodReportId) {
		my.periodReportId = periodReportId;

		my.uploadFileService.setUploadPath(
			`/Shared/Company/${my.companyId}/PeriodReport/${periodReportId}/Resource`,
		);

		const [draft, configuration, agencyUsers, customReports] =
			await Promise.all([
				my.periodReportRepository.get({
					filter: { id: periodReportId },
				}),
				my.periodReportSettingsRepository.get(),
				my.notificationService.fetchCompanyAgencyUsers(),
				my.customReportRepository.get(),
			]);

		my.sectionEditorVisitor.setCustomReports(customReports);
		my.selectedPeriod = draft.getPeriod();
		my.setDraft(draft);
		my.configuration = configuration;
		my.agencyUsers = agencyUsers;
		my.updateActions();

		my.defaultTextRepository.get().then((defaultTexts) => {
			my.commentEditor.setDefaultTexts(defaultTexts);
			my.sectionEditorVisitor.setDefaultTexts(defaultTexts);
			that.update();
		});

		that.showContent();

		if (draft.haveFiscalYearChanged()) {
			showUpdateCheckDialog();
		}
	};

	my.setDraft = function (draft) {
		my.draft = draft;

		my.sectionPicker.setPeriodReport(my.draft);
		my.checklistEditor.setChecklist(my.draft.getChecklist());

		that.onUpdateActions.trigger();

		my.draft.draftChanged.register(() =>
			my.autoSaveDraft({ updateFromRepository: false }),
		);

		my.draft.commentChanged.register(() =>
			my.autoSaveDraft({ updateFromRepository: false }),
		);
	};

	my.setLoading = function (value) {
		my.isContentLoading = value;
		that.update();
	};

	my.renderEmptyContentOn = function (html) {
		my.renderSpinnerOn(html);
	};

	my.renderLoading = function (html) {
		html.div(
			{ class: "row" },
			html.div(
				{
					class: "col-sm-4 col-sm-offset-4 text-center loading-content-progress",
				},
				my.spinner,
			),
		);
	};

	my.renderContentOn = function (html) {
		if (my.isContentLoading) {
			my.renderLoading(html);
			return;
		}

		html.div(
			{ klass: "period-report-editor" },
			(html) => my.renderPeriodSelectionOn(html),
			my.commentEditor,
			my.sectionsRenderer(),
		);
	};

	my.renderPeriodSelectionOn = function (html) {
		my.flipWidget = new FlipWidget({
			frontRenderable: buildForm(),
			backRenderable: my.makePeriodSelectionWidget(),
		});

		if (my.getPeriod().isConsecutiveMonths()) my.flipWidget.showBack();

		html.div(my.flipWidget);
		html.div({ klass: "switch-button" }, my.makeSwitchButton());
	};

	my.makeSwitchButton = function () {
		const switchButton = new SwitchButton({
			dataTestName: "custom-period",
			state: my.getPeriod().isConsecutiveMonths(),
			onStateChanged: (state) => my.onCustomPeriodSwitched(state),
		});

		return stacker(
			{ isBlock: true, align: "right" },
			spacer(_("Choose start and end months"), switchButton),
		);
	};

	my.onCustomPeriodSwitched = function (isSwitchedOn) {
		my.flipWidget.toggle();

		if (!isSwitchedOn && my.getPeriod().isConsecutiveMonths()) {
			my.onReportPeriodChange(my.regularPeriod);
		}
	};

	my.sectionsRenderer = function () {
		my.sectionWidgets = {};

		const enabledSections = my.draft
			.getSections()
			.filter((section) => section.isEnabled());

		enabledSections.forEach((section) => {
			my.sectionWidgets[section.getId()] =
				my.sectionEditorVisitor.visit(section);
		});

		return Object.keys(my.sectionWidgets).map(
			(key) => my.sectionWidgets[key],
		);
	};

	my.getPeriod = function () {
		return my.draft?.getPeriod();
	};

	my.editActionsRenderer = function () {
		const actions = [
			my.rightMenuSection(
				_("Actions"),
				my.renderPublishButtonOn,
				my.renderDeleteButtonOn,
			),
			my.rightMenuSection(_("Settings"), my.sectionPicker),
		];

		if (!my.isNotificationFeatureActive()) return actions;

		return [...actions, my.makeNotificationSection()];
	};

	my.isNotificationFeatureActive = function () {
		return optionsBag.fromId("period-report-email-notification").isActive();
	};

	my.makeNotificationSection = function () {
		const company = session.getCompany();

		const service = new PeriodReportNotificationService({
			companyId: company.getLegacyId(),
		});

		return my.rightMenuSection(
			_("Notify users"),
			makeReportNotificationButton({
				currentUser: session.getCurrentUser(),
				agencyUsers: my.agencyUsers,
				report: my.draft,
				company,
				cssClass: "btn-block btn-default",
				sendNotifications: (emailContent) =>
					service.sendNotifications({
						report: my.draft,
						emailContent,
					}),
			}),
		);
	};

	my.renderPublishButtonOn = function (html) {
		html.button(
			{
				klass: "btn btn-block btn-primary",
				"data-test": "publish",
				"data-help": "publish",
				click: () => my.publishAction(),
			},
			_("Publish"),
		);
	};

	my.renderDeleteButtonOn = function (html) {
		html.button(
			{
				klass: "btn btn-block btn-danger",
				"data-test": "delete",
				click: () => {
					my.deleteAction();
				},
			},
			_("Delete draft"),
		);
	};

	my.willDetach = function () {
		my.sectionPicker.willDetach();
	};

	my.uploadFile = async (file) => {
		try {
			const guid = await my.uploadFileService.uploadFile(file);

			return `/Shared/Company/${my.companyId}/PeriodReport/${my.periodReportId}/Resource/${guid}`;
		} catch (error) {
			notifier.error(error.message);
			throw error;
		}
	};

	my.deleteSource = (source) => {
		if (!source) return Promise.resolve();
		const guid = source.split("/").at(-1);

		return my.resourceRepository.remove({
			notify: false,
			filter: {
				guid,
				periodReportId: my.periodReportId,
			},
		});
	};

	my.onReportPeriodChange = function (period) {
		my.selectedPeriod = period;
		my.setLoading(true);

		my.preventingAutoSave(async (restoreAutoSaveFn) => {
			if (isDefaultTitleInUse(my.draft)) {
				const newTitle = buildDefaultReportTitle(period);
				my.draft.setTitle(newTitle);
				that.update();
			}
			my.draft.setPeriod(period);

			await my.updateSectionsAndDefaultAttachments();
			restoreAutoSaveFn();
			const draft = await my.saveDraft();

			my.setDraft(draft);
			my.setLoading(false);
		});
	};

	my.updateSectionsAndDefaultAttachments = function () {
		return new PeriodReportService().updateDraftSectionsAndDefaultAttachments(
			my.draft,
		);
	};

	/**
	 * Trigger an automatic save of the draft.
	 *
	 * @param {boolean} [updateFromRepository] - When true, set the draft
	 * from the repository response.
	 */
	my.autoSaveDraft = function ({ updateFromRepository = true } = {}) {
		if (my.preventAutoSave) {
			return;
		}

		eventManager.at("autoSave").trigger("schedule", () => {
			my.saveDraft()
				.then((draft) => {
					my.notifyAutoSaveSucceeded();
					if (updateFromRepository) {
						my.setDraft(draft);
						that.update();
					}
				})
				.catch(() => {
					my.notifyAutoSaveFailed();
				});
		});
	};

	my.notifyAutoSaveSucceeded = function () {
		eventManager.at("autoSave").trigger("success");
	};

	my.notifyAutoSaveFailed = function () {
		eventManager.at("autoSave").trigger("failure");
	};

	my.saveDraft = function () {
		return my.periodReportRepository.update({
			filter: {
				action: "Draft",
				id: my.draft.getId(),
			},
			model: my.draft,
			notify: false,
		});
	};

	my.publishAction = function () {
		my.confirm({
			confirmLabel: _("Publish"),
			title: _("Publish report"),
			body: _(
				"Finished? Well done. By clicking publish the report will be available for your client. If it turns out that the report not became 100% correct you can always choose to withdraw it. A withdrawn report is not visible for the client in the report archive.",
			),
			callback: () =>
				my.publish().then((publishedReport) =>
					my.redirectTo("period-report", {
						id: publishedReport.getId(),
					}),
				),
		});
	};

	my.deleteAction = function () {
		my.confirmDeletion({
			name: my.draft.getTitle(),
			callback: () => {
				my.delete().then(() => {
					my.redirectTo("period-reports");
				});
			},
		});
	};

	my.toggleChecklistItem = function (sectionId, item) {
		const isChecked = !item.isChecked();

		my.toggleChecklistItemRepository
			.update({
				filter: {
					periodReportId: my.draft.getId(),
					sectionId,
					itemId: item.getId(),
				},
				model: {
					isChecked,
				},
			})
			.then(() => {
				item.setChecked(isChecked);
				my.checklistEditor.update();
			});
	};

	my.publish = function () {
		const successMessage = _("The report is published successfully");
		const errorMessage = _("Failed to publish report");

		if (my.draft.getId()) {
			return my.periodReportRepository.update({
				filter: {
					action: "Publish",
					id: my.draft.getId(),
				},
				model: my.draft,
				successMessage,
				errorMessage,
			});
		}
		return my.periodReportRepository.save({
			filter: {
				action: "Publish",
			},
			model: my.draft,
			successMessage,
			errorMessage,
		});
	};

	my.delete = function () {
		return my.periodReportRepository.remove({
			filter: {
				id: my.draft.getId(),
			},
		});
	};

	my.hasNonEmptyChecklist = function () {
		return my.draft && my.draft.hasNonEmptyChecklist();
	};

	my.getSectionTitle = function (periodReportSectionId) {
		return my.draft.findSectionWithId(periodReportSectionId).getTitle();
	};

	my.insertComment = function (periodReportSectionId, comment) {
		const sectionWidget = my.sectionWidgets[periodReportSectionId];
		sectionWidget.updateCommentAndMakeVisible(comment);
	};

	my.preventingAutoSave = function (fn) {
		my.preventAutoSave = true;

		const restore = () => {
			my.preventAutoSave = false;
		};

		fn(restore);
	};

	my.makePeriodSelectionWidget = function () {
		return new PeriodSelectionSectionWidget({
			fiscalYears: session.getActiveYears(),
			selectedPeriod: getCurrentPeriod(),
			onChange: my.onReportPeriodChange,
		});
	};

	function isDefaultTitleInUse(report) {
		return (
			report.getTitle().toString() ===
			buildDefaultReportTitle(report.getPeriod()).toString()
		);
	}

	function showUpdateCheckDialog() {
		my.confirm({
			okLabel: _("Update report"),
			cancelLabel: _("Skip"),
			title: _("Period report out of date"),
			body: _(
				"The report is not updated with the latest values from the fiscal year. Update now?",
			),
			callback: refreshFiscalYear,
		});
	}

	function refreshFiscalYear() {
		my.setLoading(true);

		// We need to lookup up for period because the fiscal year has changed
		let period = session
			.getCompany()
			.findPeriodById(my.getPeriod().toString());

		if (period.isNullPeriod()) {
			period = session.getCompany().lastActiveMonth();
		}

		my.preventingAutoSave(async (restoreAutoSave) => {
			my.draft.setPeriod(period);

			await my.updateSectionsAndDefaultAttachments();

			const draft = await my.saveDraft();
			my.setDraft(draft);
			my.setLoading(false);

			restoreAutoSave();
		});
	}

	function buildDefaultReportTitle(selectedPeriod) {
		return _("Report for {period}", {
			period: selectedPeriod.toSmartString(),
		});
	}

	function buildPeriods() {
		let result = [];

		session
			.getCompany()
			.getYears()
			.reverse()
			.forEach((year) => {
				result = result.concat(year.getAllActivePeriods());
			});

		return result.map((period) => ({
			label: period.toSmartString(),
			value: period,
		}));
	}

	function buildForm() {
		const form = new FormBuilder({
			klass: "period-selection",
			model: {},
			showActions: false,
		});

		const items = buildPeriods();

		const selectedPeriodItem = items.find(({ value: period }) =>
			period.equals(my.selectedPeriod),
		);

		my.regularPeriod ||= selectedPeriodItem?.value || items[0].value;

		form.newDropDownField({
			label: _("Period"),
			attribute: "period",
			name: "period",
			items,
			onChange: (period) => {
				my.regularPeriod = period;
				my.onReportPeriodChange(period);
			},
			disabled: my.isContentLoading,
			selected: my.regularPeriod,
		});

		return (html) => html.div({ "data-help": "period-form" }, form);
	}

	function getCurrentPeriod() {
		const companyYears = session.getCompany().getYears();
		const draftPeriod = my.getPeriod();
		const draftYear = draftPeriod.getYear();
		const currentYear = companyYears.find((year) => year.equals(draftYear));
		return currentYear?.findEquivalentActivePeriod(draftPeriod);
	}

	that.showEmpty();
	return that;
}

export default periodReportEditorDocument;
